import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Lottie from 'lottie-react';
import { Stack, Box, Typography, LinearProgress, CircularProgress, Portal } from '@mui/material';
import PropTypes from 'prop-types';

import exportData from '@assets/lotties/export-loader.json';
import importData from '@assets/lotties/import-loader.json';
import fielderLoading from '@assets/lotties/loadingFielder.json';
import { selectLoadingConfig } from '@core/store/modules/loading/loading.selector';
import 'FielderUtils/style.css';
import LoadingConst from 'OldComponents/Loading/LoadingConst';

const getAnimationData = (icon) => {
  if (icon === LoadingConst.IMPORT) return importData;
  if (icon === LoadingConst.EXPORT) return exportData;
  return fielderLoading;
};

const toPx = (v, fallback) =>
  typeof v === 'number' ? v : (typeof v === 'string' && v.endsWith('px') ? parseInt(v, 10) : fallback);

const Loading = ({
                   size = null,
                   thickness = 5,
                   inline = false,
                   iconOverride = null,
                   textOverride = '',
                   progressOverride = null,
                 }) => {
  const reduxConfig = useSelector(selectLoadingConfig) || {};
  const { icon, text, progress } = reduxConfig;

  const [hasError, setHasError] = useState(false);
  const [anim, setAnim] = useState(fielderLoading);

  const lottieRef = useRef(null);

  // choose the right JSON safely
  useEffect(() => {
    try {
      const data = getAnimationData(iconOverride || icon) || fielderLoading;
      setAnim(typeof data === 'string' ? JSON.parse(data) : data);
      setHasError(false);
    } catch (e) {
      console.error('Failed to load animation:', e);
      setHasError(true);
    }
  }, [icon, iconOverride]);

  // when animation (or visibility) changes, force resize + play
  useEffect(() => {
    if (!lottieRef.current) return;
    // run after layout to avoid measuring a 0px container
    const id = requestAnimationFrame(() => {
      try {
        lottieRef.current.resize?.();
        // restart from frame 0 to ensure layers flip visible
        lottieRef.current.goToAndPlay?.(0, true);
      } catch {}
    });
    return () => cancelAnimationFrame(id);
  }, [anim, inline]);

  // computed dimensions
  const pxSizeInline = useMemo(() => toPx(size, 40), [size]);
  const pxSizeFull = useMemo(() => toPx(size, 200), [size]);

  // ---------- inline variant ----------
  if (inline) {
    return !hasError ? (
      <Lottie
        lottieRef={lottieRef}
        animationData={anim}
        loop
        autoplay
        renderer="svg"
        rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
        style={{ width: pxSizeInline, height: pxSizeInline, maxWidth: pxSizeInline }}
      />
    ) : (
      <CircularProgress size={pxSizeInline} thickness={thickness} sx={{ color: '#FA7FAB' }} />
    );
  }

  // ---------- full-screen overlay ----------
  const showProgress =
    Number.isFinite(progressOverride ?? progress) &&
    Math.round(progressOverride ?? progress) < 100;

  return (
    <Portal>
      <Box
        id="fielder-loading"
        role="status"
        aria-live="polite"
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh', // important: give real height
          bgcolor: 'rgba(255,255,255,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: (theme) => theme.zIndex.modal + 10,
          overflow: 'hidden',
        }}
      >
        <Stack direction="column" justifyContent="center" alignItems="center" spacing={6} sx={{ width: '100%', height: '100%' }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            // give the lottie box a concrete height so svg isn't born at 0px
            sx={{ width: 'min(200px, 60vw)', height: pxSizeFull, minHeight: 160, mt: 2 }}
          >
            {!hasError ? (
              <Lottie
                lottieRef={lottieRef}
                animationData={anim}
                loop
                autoplay
                renderer="svg"
                rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <CircularProgress size={Math.max(pxSizeFull, 160)} thickness={thickness} sx={{ color: '#FA7FAB' }} />
            )}
          </Box>

          {(textOverride || text) && (
            <Typography variant="body1" textAlign="center" px={2} sx={{ wordBreak: 'break-word' }}>
              {textOverride || text}
            </Typography>
          )}

          {showProgress && (
            <Box width={{ xs: '80%', sm: '60%', md: '50%', lg: '40%' }} textAlign="center" mt={1}>
              <LinearProgress
                variant="determinate"
                value={Math.round(progressOverride ?? progress)}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#d33b6e',
                    animation: 'progress-bar-stripes 0.5s linear infinite',
                    backgroundSize: '1rem 1rem',
                  },
                  backgroundColor: '#FA7FAB',
                }}
              />
              <Typography
                variant="body2"
                mt={1}
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '30px',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  textAlign: 'center',
                  color: 'var(--dark-grey-blue)',
                }}
              >
                {`${Math.round(progressOverride ?? progress)}%`}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Portal>
  );
};

Loading.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  thickness: PropTypes.number,
  inline: PropTypes.bool,
  iconOverride: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  textOverride: PropTypes.string,
  progressOverride: PropTypes.number,
};

Loading.defaultProps = {
  size: null,
  thickness: 5,
  inline: false,
  iconOverride: null,
  textOverride: '',
  progressOverride: null,
};

export default Loading;
